<template>
    <div id="product" class="product-mobile">
        <ImageSwiper :images="productDetails.images" />

        <div class="container">
            <ProductRatingControls
                v-if="$store.getters.isMainShop && !isValueVoucher"
                :product-id="productDetails.id"
                :product-category="productDetails.productCategory"
                class="ml-auto pt-1"
            />

            <section v-if="productDetails.availableUntil" class="product-mobile__countdown-section">
                <CountdownTimer
                    :available-until="productDetails.availableUntil"
                    class="mx-5"
                    @expired="isExpired = true"
                />
            </section>

            <template v-if="showHotelPrimary">
                <Classification
                    v-skeleton="hotelDetails.hotelClassification"
                    :hotelClassification="hotelDetails.hotelClassification || {}"
                />
                <Link v-if="hotelDetails.hotelPageVisible" :link="hotelLink" type="hotel">
                    <h1 class="hotel-title">{{ hotelDetails.name }}</h1>
                </Link>
            </template>
            <!-- TODO: show banner text -->
            <component :is="titleTag" class="product__title" :class="{ 'text-uppercase': !showHotelPrimary }">
                {{ productDetails.title }}
            </component>

            <DetailsSection v-if="showFeatures">
                <FeatureIcons :features="productDetails.hotelTags" />
            </DetailsSection>

            <DetailsSection v-if="showServices" :no-border="productDetails.hasAvailabilityPreview">
                <template v-slot:header>
                    {{ servicesSectionHeader }}
                </template>

                <CheckList :items="productDetails.services" />
            </DetailsSection>

            <AvailabilityPreview v-if="productDetails.hasAvailabilityPreview" :product-id="productDetails.id" />

            <DetailsSection v-if="showVoucherInfos">
                <template v-slot:header>
                    {{ voucherInfosSectionHeader }}
                </template>

                <TextList :items="productDetails.voucherInfos" />
            </DetailsSection>

            <CollapsibleItem v-if="!!productDetails.additionalDescription" append-icon nested>
                <template v-slot:summary.title>
                    <span class="text-uppercase">{{ $t('VoucherDetails.tab.additionalDescription') }}</span>
                </template>

                <template v-slot:expandable>
                    <DetailsSection>
                        <AdditionalDescription :descriptionHtml="productDetails.additionalDescription" />
                    </DetailsSection>
                </template>
            </CollapsibleItem>

            <CollapsibleItem v-if="!!productDetails.latLon" append-icon nested>
                <template v-slot:summary.title>
                    <span class="text-uppercase">{{ mapSectionHeader }}</span>
                </template>

                <template v-slot:expandable>
                    <DetailsSection>
                        <HotelAddress :hotelDetails="hotelDetails" :is-other-product="isOtherProduct" class="mb-2" />
                        <HotelMap v-if="showMap" :center="productDetails.latLon" :zoom="9" :markers="[marker]" />
                    </DetailsSection>
                </template>
            </CollapsibleItem>

            <CollapsibleItem v-if="showHotelSection" append-icon nested>
                <template v-slot:summary.title>
                    <span class="text-uppercase">{{ descriptionSectionHeader }}</span>
                </template>

                <template v-slot:expandable>
                    <DetailsSection v-if="showHotelDescription">
                        <template v-slot:header>
                            {{ hotelDescriptionSectionHeader }}
                        </template>

                        <Description :descriptionHtml="productDetails.hotelDescription" collapse></Description>
                    </DetailsSection>

                    <DetailsSection v-if="productDetails.description">
                        <template v-slot:header>{{ $t('ProductDetails.section.aboutOffer') }}</template>
                        <Description :descriptionHtml="productDetails.description"></Description>
                    </DetailsSection>

                    <DetailsSection v-if="!showMap">
                        <template v-slot:header>{{ $t('HotelAddress.term.provider') }}</template>
                        <h6 class="mb-2">Animod GmbH</h6>
                        <HotelAddress :hotelDetails="hotelDetails" />
                    </DetailsSection>

                    <DetailsSection v-if="showFacilities" class="pb-0">
                        <template v-slot:header>{{ $t('ProductDetails.section.featuresAndAmenities') }}</template>
                        <Facilities
                            :extraServices="productDetails.extraServices"
                            :additionalHotelInfos="productDetails.additionalHotelInfos"
                            :facilityTags="productDetails.facilityTags"
                        >
                        </Facilities>
                    </DetailsSection>
                </template>
            </CollapsibleItem>

            <NewsletterSection source="ProductDetailsPage" />

            <DetailsSection v-if="productDetails.faqContent" no-border>
                <template #header>{{ $t('ProductDetails.section.faq') }}</template>
                <Accordion :data="productDetails.faqContent" filled-titles />
            </DetailsSection>
        </div>

        <DialogSwipe direction="bottom" v-if="showShareMobile" @close="$store.commit('setShowShare', false)">
            <Share :title="productDetails.title" :pinterestImage="productDetails.teaserImageUrl" class="share-mobile">
            </Share>
        </DialogSwipe>

        <template v-if="isMivoBidding">
            <ProductFooter
                ref="footer"
                @click="showSelectionDialog = true"
                :buttonText="buttonText"
                class="netto-product-footer"
            />
            <DialogSwipe :show="showSelectionDialog" direction="bottom" @close="showSelectionDialog = false">
                <MivoProductSelect
                    :productDetails="productDetails"
                    :buyerProtectionEnabled="product.buyerProtectionEnabled"
                />
            </DialogSwipe>
        </template>
        <template v-else>
            <ProductFooterMobile
                ref="footer"
                @click="showSelectionDialog = true"
                :button-text="specialBottomButtonText"
                :checkoutAvailableFrom="productDetails.checkoutAvailableFrom"
            >
                <PriceNew
                    v-if="showFooterPrice"
                    :price="productDetails.price"
                    :preferred-mode="isPartnerShopWithPackagePricing ? 'minimumPrice' : undefined"
                    small-prefix
                    show-prefix
                    :prefix="pricePrefix"
                />
            </ProductFooterMobile>

            <DialogSwipe :show="showSelectionDialog" direction="bottom" fullscreen disable-swipe no-gutters no-mask>
                <ProductSelectMobile
                    v-if="hasProductDetails"
                    :product-details="productDetails"
                    @close="showSelectionDialog = false"
                />
            </DialogSwipe>
        </template>
    </div>
</template>

<script>
import ImageSwiper from '@/components/swiper/ImageSwiper';
import Classification from '@/components/Classification';
import DetailsSection from '@/components/DetailsSection';
import Description from '@/components/Description';
import Facilities from '@/components/Facilities';
import FeatureIcons from '@/components/FeatureIcons';
import AdditionalDescription from '@/components/product/AdditionalDescription';
import CheckList from '@/components/product/CheckList';
import TextList from '@/components/product/TextList';
import HotelMap from '@/components/map/HotelMap';
import HotelAddress from '@/components/HotelAddress';
import DialogSwipe from '@/components/mobile/DialogSwipe';
import ProductSelectMobile from '@/components/product/ProductSelectMobile';
import ProductFooterMobile from '@/components/mobile/ProductFooterMobile';
import skeleton from '@/directives/skeleton';
import productStickyFooter from '~/mixins/injectStickyFooter';
import productDetails from '@/mixins/productDetails';
import CollapsibleItem from '@/components/utility/CollapsibleItem';
import NewsletterSection from '@/components/newsletter/NewsletterSection';
import Accordion from '@/components/grid/Accordion';
import ProductRatingControls from '@/components/product/rating/ProductRatingControls';
import PriceNew from '@/components/PriceNew.vue';
import AvailabilityPreview from '@/components/product/AvailabilityPreview.vue';
import CountdownTimer from '@/components/product/countdown/CountdownTimer.vue';
import ProductFooter from '@/components/mobile/ProductFooterMobile.vue';
import NettoProductSelect from '@/themes/nettovorteilswelt/components/product/NettoProductSelect.vue';

export default {
    name: 'ProductDetailsMobile',
    directives: {
        skeleton
    },
    mixins: [productDetails, productStickyFooter()],
    components: {
        NettoProductSelect,
        ProductFooter,
        CountdownTimer,
        AvailabilityPreview,
        PriceNew,
        NewsletterSection,
        CollapsibleItem,
        Classification,
        DetailsSection,
        Description,
        Facilities,
        FeatureIcons,
        AdditionalDescription,
        CheckList,
        TextList,
        HotelMap,
        HotelAddress,
        ProductFooterMobile,
        DialogSwipe,
        ProductSelectMobile,
        ImageSwiper,
        Accordion,
        ProductRatingControls,
        //Async components. Reducing vendors production bundle size
        MivoProductSelect: () =>
            import(/* webpackChunkName: "mivo-product-select" */ '@/themes/mivo/components/product/MivoProductSelect'),
        Share: () => import(/* webpackChunkName: "share" */ '@/components/Share')
    },
    data() {
        return {
            showShare: false,
            showSelectionDialog: false
        };
    },
    computed: {
        hasProductDetails() {
            return !!Object.keys(this.productDetails).length;
        },
        showShareMobile() {
            return this.$store.state.showShareMobile && this.$store.state.ident !== 'www.animod.nl';
        },
        hotelLink() {
            const { id, webname } = this.hotelDetails;
            return {
                hotelId: id,
                webnames: [webname]
            };
        },
        isMivoBidding() {
            return this.$store.state.ident === 'mivo' && this.productDetails.auctionOffer;
        },
        showFooterPrice() {
            const perNightPrice = this.$getSafe(
                this.productDetails,
                'price.regularPriceValues.minimumPerPersonPerNightPrice.perPersonPerNightPrice'
            );
            const packagePrice = this.$getSafe(this.productDetails, 'price.regularPriceValues.minimumPrice.price');
            return !this.productDetails.soldOut && !this.isExpired && (perNightPrice || packagePrice);
        },
        isPartnerShopWithPackagePricing() {
            return this.$store.getters.isPartnerShopWithPackagePricing;
        },
        hasMultipleVariants() {
            return (
                this.$getSafe(this.productDetails, 'variantsWithScheduledArrival.length') > 1 ||
                this.$getSafe(this.productDetails, 'variants.length') > 1
            );
        },
        pricePrefix() {
            return this.hasMultipleVariants ? this.$t('Price.prefixDefault') : this.$t('Price.prefixDiscount');
        },
        showHotelSection() {
            return this.showHotelDescription || this.productDetails.description || this.showFacilities;
        },
        titleTag() {
            return this.showHotelPrimary ? 'h2' : 'h1';
        },
        specialBottomButtonText() {
            return this.isExpired ? this.$t('Zur Warteliste') : undefined; // make sure not to use null here to prevent empty button
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';

#product {
    background-color: var(--white);

    .hotel-title,
    .classification,
    .product__title {
        font-size: 18px;
        line-height: 1.2em;
    }

    .hotel-title {
        margin-top: 5px;
        font-weight: bold;
        color: var(--body-color);
        text-decoration: underline;
        margin-bottom: 0;
    }

    .product__title {
        font-weight: bold;
        margin-top: 10px;
    }

    .classification {
        margin-top: variables.$grid-padding;

        &.skeleton {
            width: 100%;
        }
    }

    .hotel-link {
        color: var(--body-color);
        font-size: 20px;
        font-weight: bold;
        margin-top: variables.$grid-padding;
        display: block;
        text-decoration: underline;
    }

    ::v-deep {
        .classification {
            .animod-stars,
            .superior {
                font-size: 14px;
                margin-left: 10px;
            }
        }
    }

    .collapsible-item {
        $collapsible: &;
        margin: -1px (-(variables.$grid-padding)) 0;
        padding: 0 variables.$grid-padding;

        border-top: 1px solid var(--border-color);
        border-bottom: 1px solid var(--border-color);

        & .details-section {
            &:first-child {
                padding-top: 0;
            }

            &:last-child {
                border-bottom: none;
            }
        }

        ::v-deep {
            .collapsible-item__summary {
                padding-top: 10px;
                padding-bottom: 10px;
                font-size: 18px;
            }

            .collapsible-item__content {
                padding-top: 0;
                padding-bottom: 0;
            }
        }
    }

    .product-mobile {
        &__countdown-section {
            margin-top: 1rem;
            text-align: center;
            background-color: var(--product-select-header-bg);
            border-radius: 0.25rem;
            padding: 1rem;
        }
    }
}
</style>
